import React, { useState, useEffect } from 'react';
import { X, Save, AlertTriangle, Edit2 } from 'lucide-react';
import { API_URL } from '../../services/api';
import PierresEditor from './PierresEditor';

const TYPES_BIJOU = ['BAGUE', 'COLLIER', 'BRACELET', 'BOUCLE OREILLE', 'PENDENTIF', 'ALLIANCE', 'CHEVALIERE', 'GOURMETTE', 'BROCHE', 'AUTRE'];
const COULEURS_OR = ['JAUNE', 'BLANC', 'ROSE', 'GRIS', 'BICOLORE'];

export default function EditDevisModal({ devis, onClose, onSaved }) {
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [details, setDetails] = useState(null);
  const [loadingDetails, setLoadingDetails] = useState(false);
  const [pierres, setPierres] = useState([]);

  // Charger le détail complet du devis (composants, matières, pierres, ouvriers, couts...)
  useEffect(() => {
    if (!devis?.id) return;
    let cancelled = false;
    setLoadingDetails(true);
    fetch(`${API_URL}/devis/${devis.id}`, {
      headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
    })
      .then(r => r.ok ? r.json() : null)
      .then(data => { if (!cancelled && data) setDetails(data); })
      .catch(() => {})
      .finally(() => { if (!cancelled) setLoadingDetails(false); });
    return () => { cancelled = true; };
  }, [devis?.id]);

  // Mapper les pierres du devis (GET) vers la forme editable de PierresEditor
  useEffect(() => {
    if (!details) { setPierres([]); return; }
    setPierres((details.pierres || []).map(p => ({
      reference: p.reference || '',
      type: p.type_pierre || '',
      poids_carats: p.poids_unitaire_carats ?? '',
      quantite: p.nb_pierres || 1,
      stock_id: p.stock_matiere_id,
      prix_achat_unitaire: p.prix_unitaire ?? '',
    })));
  }, [details]);

  useEffect(() => {
    if (!devis) return;
    setForm({
      type_bijou:              devis.type_bijou || '',
      couleur_or:              devis.couleur_or || '',
      type_creation:           devis.type_creation || 'BIJOUTERIE',
      client_nom:              devis.client_nom || '',
      collection_nom:          devis.collection_nom || '',
      reference_produit:       devis.reference_produit || '',
      description:             devis.description || '',
      notes:                   devis.notes || '',
      date_livraison_souhaitee: devis.date_livraison_souhaitee
        ? new Date(devis.date_livraison_souhaitee).toISOString().split('T')[0]
        : '',
      genre:                   devis.genre || '',
      poids_metal:             devis.poids_metal || '',
      poids_unitaire:          devis.poids_unitaire || '',
      quantite_pieces:         devis.quantite_pieces || 1,
      coefficient_marge:       devis.coefficient_marge || 2.6,
      prix_vente_final:        devis.prix_vente_final || '',
      rhodiage:                devis.rhodiage ? 1 : 0,
      ecrin:                   devis.ecrin ? 1 : 0,
      gravure_beldi:           devis.gravure_beldi ? 1 : 0,
      garantie:                devis.garantie ? 1 : 0,
      fonte2:                  devis.fonte2 ? 1 : 0,
    });
  }, [devis]);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const handleSave = async () => {
    setSaving(true);
    setError('');
    try {
      const res = await fetch(`${API_URL}/devis/${devis.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
        body: JSON.stringify({ ...form, pierres }),
      });
      const data = await res.json();
      if (!res.ok) {
        setError(data.error || 'Erreur lors de la sauvegarde');
        return;
      }
      onSaved(data.devis || { ...devis, ...form });
      onClose();
    } catch (e) {
      setError('Erreur réseau');
    } finally {
      setSaving(false);
    }
  };

  if (!devis) return null;

  const ref = devis.reference_produit || devis.numero_devis || `#${devis.id}`;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] flex flex-col">

        {/* ── Header ── */}
        <div className="flex items-center justify-between px-6 py-4 border-b">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-blue-100 rounded-lg">
              <Edit2 className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <h2 className="text-lg font-bold text-gray-900">
                Modifier le devis — <span className="font-mono text-indigo-600">{ref}</span>
              </h2>
              <p className="text-xs text-gray-500">Modifications avant lancement en production</p>
            </div>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg transition">
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        {/* ── Corps scrollable ── */}
        <div className="overflow-y-auto flex-1 px-6 py-5 space-y-5">

          {/* Bijou */}
          <div>
            <h3 className="text-xs font-bold uppercase tracking-wide text-gray-400 mb-3">Bijou</h3>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Type de bijou</label>
                <select
                  value={form.type_bijou}
                  onChange={e => set('type_bijou', e.target.value)}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-400 focus:outline-none"
                >
                  {TYPES_BIJOU.map(t => <option key={t} value={t}>{t}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Couleur / Métal</label>
                <select
                  value={form.couleur_or}
                  onChange={e => set('couleur_or', e.target.value)}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-400 focus:outline-none"
                >
                  {COULEURS_OR.map(c => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Type</label>
                <select
                  value={form.type_creation}
                  onChange={e => set('type_creation', e.target.value)}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-400 focus:outline-none"
                >
                  <option value="BIJOUTERIE">Bijouterie (stock)</option>
                  <option value="CLIENT">Commande client</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {form.type_creation === 'CLIENT' ? 'Client' : 'Collection'}
                </label>
                <input
                  type="text"
                  value={form.type_creation === 'CLIENT' ? form.client_nom : form.collection_nom}
                  onChange={e => set(form.type_creation === 'CLIENT' ? 'client_nom' : 'collection_nom', e.target.value)}
                  placeholder={form.type_creation === 'CLIENT' ? 'Nom du client' : 'Nom de la collection'}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-400 focus:outline-none"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Référence modèle</label>
                <input
                  type="text"
                  value={form.reference_produit}
                  onChange={e => set('reference_produit', e.target.value)}
                  placeholder="Ex: COLJ24"
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm font-mono focus:ring-2 focus:ring-blue-400 focus:outline-none"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Genre</label>
                <select
                  value={form.genre}
                  onChange={e => set('genre', e.target.value)}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-400 focus:outline-none"
                >
                  <option value="">Non défini</option>
                  <option value="FEMME">Femme</option>
                  <option value="HOMME">Homme</option>
                  <option value="ENFANT">Enfant</option>
                  <option value="MIXTE">Mixte</option>
                </select>
              </div>
              <div className="col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                <textarea
                  value={form.description}
                  onChange={e => set('description', e.target.value)}
                  rows={2}
                  placeholder="Description du bijou..."
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-400 focus:outline-none"
                />
              </div>
            </div>
          </div>

          {/* Note importante */}
          <div>
            <label className="block text-sm font-medium text-red-700 mb-1 flex items-center gap-1.5">
              <AlertTriangle className="w-4 h-4" />
              Note importante (visible en rouge sur la fiche fabrication)
            </label>
            <textarea
              value={form.notes}
              onChange={e => set('notes', e.target.value)}
              rows={2}
              placeholder="Ex: Taille 54, gravure intérieure, attention rhodiage renforcé..."
              className="w-full border-2 border-red-300 bg-red-50 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-red-400 focus:outline-none"
            />
          </div>

          {/* Production */}
          <div>
            <h3 className="text-xs font-bold uppercase tracking-wide text-gray-400 mb-3">Fabrication</h3>
            <div className="grid grid-cols-3 gap-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Quantité pièces</label>
                <input
                  type="number" min={1}
                  value={form.quantite_pieces}
                  onChange={e => set('quantite_pieces', parseInt(e.target.value) || 1)}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-400 focus:outline-none"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Poids / pièce (g)</label>
                <input
                  type="number" step="0.001" min={0}
                  value={form.poids_unitaire}
                  onChange={e => set('poids_unitaire', e.target.value)}
                  placeholder="0.000"
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-400 focus:outline-none"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Poids métal total (g)</label>
                <input
                  type="number" step="0.001" min={0}
                  value={form.poids_metal}
                  onChange={e => set('poids_metal', e.target.value)}
                  placeholder="0.000"
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-400 focus:outline-none"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Coeff. marge</label>
                <input
                  type="number" step="0.1" min={1}
                  value={form.coefficient_marge}
                  onChange={e => set('coefficient_marge', e.target.value)}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-400 focus:outline-none"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Prix vente final (DH)</label>
                <input
                  type="number" step="0.01" min={0}
                  value={form.prix_vente_final}
                  onChange={e => set('prix_vente_final', e.target.value)}
                  placeholder="0.00"
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-400 focus:outline-none"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Date livraison</label>
                <input
                  type="date"
                  value={form.date_livraison_souhaitee}
                  onChange={e => set('date_livraison_souhaitee', e.target.value)}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-400 focus:outline-none"
                />
              </div>
            </div>
          </div>

          {/* ═══════ Détails du devis (lecture seule) ═══════ */}
          {loadingDetails && (
            <div className="text-xs text-gray-400 italic">Chargement des détails du devis…</div>
          )}

          {details && (
            <div className="space-y-4 border-t-2 border-dashed border-gray-200 pt-5 mt-2">
              <div className="text-[11px] font-bold text-gray-500 bg-gray-100 rounded px-2 py-1 inline-block">
                📋 Contenu du devis d'origine (lecture seule — édition dans le formulaire complet bientôt)
              </div>

              {/* Étapes de fabrication */}
              {details.couts_fabrication?.length > 0 && (
                <div>
                  <h3 className="text-xs font-bold uppercase tracking-wide text-indigo-600 mb-2">⚙️ Étapes de fabrication ({details.couts_fabrication.length})</h3>
                  <div className="border border-indigo-100 rounded-lg overflow-hidden">
                    <table className="w-full text-xs">
                      <thead className="bg-indigo-50">
                        <tr>
                          <th className="text-left px-3 py-1.5 font-semibold text-indigo-700">Étape</th>
                          <th className="text-right px-3 py-1.5 font-semibold text-indigo-700">Qté</th>
                          <th className="text-right px-3 py-1.5 font-semibold text-indigo-700">Prix unit.</th>
                          <th className="text-right px-3 py-1.5 font-semibold text-indigo-700">Total</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-indigo-50">
                        {details.couts_fabrication.map((c, i) => (
                          <tr key={i}>
                            <td className="px-3 py-1.5 text-gray-700">{c.nom || '—'}</td>
                            <td className="px-3 py-1.5 text-right text-gray-600">{c.quantite ?? '—'}</td>
                            <td className="px-3 py-1.5 text-right text-gray-600">{c.prix_unitaire ? `${parseFloat(c.prix_unitaire).toFixed(2)} DH` : '—'}</td>
                            <td className="px-3 py-1.5 text-right font-semibold text-indigo-700">{(c.quantite && c.prix_unitaire)
                              ? `${(parseFloat(c.quantite) * parseFloat(c.prix_unitaire)).toFixed(2)} DH`
                              : (c.prix_total ? `${parseFloat(c.prix_total).toFixed(2)} DH` : '—')}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* Matières premières */}
              {details.matieres?.length > 0 && (
                <div>
                  <h3 className="text-xs font-bold uppercase tracking-wide text-amber-600 mb-2">🥇 Matières premières ({details.matieres.length})</h3>
                  <div className="border border-amber-100 rounded-lg overflow-hidden">
                    <table className="w-full text-xs">
                      <thead className="bg-amber-50">
                        <tr>
                          <th className="text-left px-3 py-1.5 font-semibold text-amber-700">Référence</th>
                          <th className="text-left px-3 py-1.5 font-semibold text-amber-700">Type</th>
                          <th className="text-right px-3 py-1.5 font-semibold text-amber-700">Quantité</th>
                          <th className="text-right px-3 py-1.5 font-semibold text-amber-700">Prix total</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-amber-50">
                        {details.matieres.map((m, i) => (
                          <tr key={i}>
                            <td className="px-3 py-1.5 font-mono text-gray-700">{m.reference || '—'}</td>
                            <td className="px-3 py-1.5 text-gray-600">{m.type_matiere || '—'}</td>
                            <td className="px-3 py-1.5 text-right text-gray-600">{m.quantite ? parseFloat(m.quantite).toFixed(3) : '—'}</td>
                            <td className="px-3 py-1.5 text-right font-semibold text-amber-700">{(m.quantite && m.prix_unitaire)
                              ? `${(parseFloat(m.quantite) * parseFloat(m.prix_unitaire)).toFixed(2)} DH`
                              : (m.prix_total ? `${parseFloat(m.prix_total).toFixed(2)} DH` : '—')}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* Pierres (MODIFIABLE) */}
              <div>
                <h3 className="text-xs font-bold uppercase tracking-wide text-purple-600 mb-2">💎 Pierres (modifiable)</h3>
                <PierresEditor pierres={pierres} onChange={setPierres} />
              </div>

              {/* Ouvriers / Ressources internes */}
              {details.ouvriers?.length > 0 && (
                <div>
                  <h3 className="text-xs font-bold uppercase tracking-wide text-green-600 mb-2">👷 Ouvriers / Main d'œuvre ({details.ouvriers.length})</h3>
                  <div className="border border-green-100 rounded-lg overflow-hidden">
                    <table className="w-full text-xs">
                      <thead className="bg-green-50">
                        <tr>
                          <th className="text-left px-3 py-1.5 font-semibold text-green-700">Ouvrier</th>
                          <th className="text-right px-3 py-1.5 font-semibold text-green-700">Temps (min)</th>
                          <th className="text-right px-3 py-1.5 font-semibold text-green-700">Coût total</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-green-50">
                        {details.ouvriers.map((o, i) => (
                          <tr key={i}>
                            <td className="px-3 py-1.5 text-gray-700">{o.prenom || ''} {o.nom || '—'}</td>
                            <td className="px-3 py-1.5 text-right text-gray-600">{o.temps_minutes || '—'}</td>
                            <td className="px-3 py-1.5 text-right font-semibold text-green-700">{o.cout_total ? `${parseFloat(o.cout_total).toFixed(2)} DH` : '—'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* Packaging */}
              {details.packaging?.length > 0 && (
                <div>
                  <h3 className="text-xs font-bold uppercase tracking-wide text-pink-600 mb-2">📦 Packaging ({details.packaging.length})</h3>
                  <div className="border border-pink-100 rounded-lg overflow-hidden">
                    <table className="w-full text-xs">
                      <thead className="bg-pink-50">
                        <tr>
                          <th className="text-left px-3 py-1.5 font-semibold text-pink-700">Type</th>
                          <th className="text-right px-3 py-1.5 font-semibold text-pink-700">Quantité</th>
                          <th className="text-right px-3 py-1.5 font-semibold text-pink-700">Coût</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-pink-50">
                        {details.packaging.map((p, i) => (
                          <tr key={i}>
                            <td className="px-3 py-1.5 text-gray-700">{p.type || p.type_packaging || '—'}</td>
                            <td className="px-3 py-1.5 text-right text-gray-600">{p.quantite || 1}</td>
                            <td className="px-3 py-1.5 text-right font-semibold text-pink-700">{p.cout_total ? `${parseFloat(p.cout_total).toFixed(2)} DH` : '—'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* Façon si actif */}
              {details.facon_actif == 1 && (
                <div>
                  <h3 className="text-xs font-bold uppercase tracking-wide text-orange-600 mb-2">🛠️ Façon</h3>
                  <div className="bg-orange-50 border border-orange-100 rounded-lg px-3 py-2 text-xs text-gray-700">
                    <div>{details.facon_description || "Façon (main d'œuvre)"}</div>
                    <div className="font-semibold text-orange-700">Coefficient : {details.facon_coefficient}</div>
                  </div>
                </div>
              )}

              {!details.couts_fabrication?.length && !details.matieres?.length &&
               !details.pierres?.length && !details.ouvriers?.length &&
               !details.packaging?.length && details.facon_actif != 1 && (
                <div className="text-xs text-gray-400 italic bg-gray-50 rounded p-3 text-center">
                  Aucun composant détaillé enregistré sur ce devis.
                </div>
              )}
            </div>
          )}

          {/* Options finition */}
          <div>
            <h3 className="text-xs font-bold uppercase tracking-wide text-gray-400 mb-3">Options finition</h3>
            <div className="grid grid-cols-2 sm:grid-cols-5 gap-2">
              {[
                { key: 'rhodiage',     label: 'Rhodiage' },
                { key: 'ecrin',        label: 'Écrin' },
                { key: 'gravure_beldi',label: 'Gravure beldi' },
                { key: 'garantie',     label: 'Garantie' },
                { key: 'fonte2',       label: '2ème fonte' },
              ].map(({ key, label }) => (
                <label
                  key={key}
                  className={`flex items-center gap-2 cursor-pointer p-2.5 rounded-xl border-2 transition ${
                    form[key] ? 'border-blue-400 bg-blue-50' : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <input
                    type="checkbox"
                    checked={!!form[key]}
                    onChange={e => set(key, e.target.checked ? 1 : 0)}
                    className="accent-blue-500 w-4 h-4"
                  />
                  <span className="text-sm font-medium text-gray-700">{label}</span>
                </label>
              ))}
            </div>
          </div>

        </div>

        {/* ── Footer ── */}
        <div className="px-6 py-4 border-t flex items-center justify-between gap-3">
          {error && (
            <p className="text-sm text-red-600 flex items-center gap-1.5">
              <AlertTriangle className="w-4 h-4" /> {error}
            </p>
          )}
          <div className="flex gap-3 ml-auto">
            <button
              onClick={onClose}
              className="px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 transition"
            >
              Annuler
            </button>
            <button
              onClick={handleSave}
              disabled={saving}
              className="flex items-center gap-2 px-5 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-semibold shadow transition disabled:opacity-50"
            >
              <Save className="w-4 h-4" />
              {saving ? 'Sauvegarde…' : 'Enregistrer'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
